const SIMULATED_SUBDOMAIN_KEY = 'simulated_subdomain';
const LAST_SELECTED_SUBDOMAIN_KEY = 'spatuno_last_org';
const LEGACY_SUBDOMAIN_KEY = 'casdoor_last_org';
const AUTH_REALM_KEY = 'spatuno_auth_realm';

const safeSetItem = (storage: Storage | undefined, key: string, value: string) => {
  try {
    storage?.setItem(key, value);
  } catch (error) {
    console.warn('[subdomainPersistence] Falha ao salvar valor:', error);
  }
};

const safeRemoveItem = (storage: Storage | undefined, key: string) => {
  try {
    storage?.removeItem(key);
  } catch (error) {
    console.warn('[subdomainPersistence] Falha ao remover valor:', error);
  }
};

const safeGetItem = (storage: Storage | undefined, key: string): string | null => {
  try {
    return storage?.getItem(key) ?? null;
  } catch (error) {
    console.warn('[subdomainPersistence] Falha ao ler valor:', error);
    return null;
  }
};

export const persistSubdomainSelection = (value: string | null) => {
  if (typeof window === 'undefined') return;

  if (value) {
    safeSetItem(window.localStorage, SIMULATED_SUBDOMAIN_KEY, value);
    safeSetItem(window.sessionStorage, LAST_SELECTED_SUBDOMAIN_KEY, value);
    safeRemoveItem(window.sessionStorage, LEGACY_SUBDOMAIN_KEY);
  } else {
    safeRemoveItem(window.localStorage, SIMULATED_SUBDOMAIN_KEY);
    safeRemoveItem(window.sessionStorage, LAST_SELECTED_SUBDOMAIN_KEY);
    safeRemoveItem(window.sessionStorage, LEGACY_SUBDOMAIN_KEY);
  }
};

export const getPersistedSubdomain = (): string | null => {
  if (typeof window === 'undefined') return null;

  const sessionValue = safeGetItem(window.sessionStorage, LAST_SELECTED_SUBDOMAIN_KEY);
  if (sessionValue) return sessionValue;

  // Migração: ler da chave legada e persistir na nova
  const legacyValue = safeGetItem(window.sessionStorage, LEGACY_SUBDOMAIN_KEY);
  if (legacyValue) {
    safeSetItem(window.sessionStorage, LAST_SELECTED_SUBDOMAIN_KEY, legacyValue);
    safeRemoveItem(window.sessionStorage, LEGACY_SUBDOMAIN_KEY);
    return legacyValue;
  }

  return safeGetItem(window.localStorage, SIMULATED_SUBDOMAIN_KEY);
};

export const getSimulatedSubdomain = (): string | null => {
  if (typeof window === 'undefined') return null;
  return safeGetItem(window.localStorage, SIMULATED_SUBDOMAIN_KEY);
};

/** Remove chaves de subdomain persistido (usado no logout). Limpa nova e legada. */
export const clearPersistedSubdomainKeys = () => {
  if (typeof window === 'undefined') return;
  safeRemoveItem(window.localStorage, LAST_SELECTED_SUBDOMAIN_KEY);
  safeRemoveItem(window.sessionStorage, LAST_SELECTED_SUBDOMAIN_KEY);
  safeRemoveItem(window.localStorage, LEGACY_SUBDOMAIN_KEY);
  safeRemoveItem(window.sessionStorage, LEGACY_SUBDOMAIN_KEY);
};

/**
 * Resolve o subdomain/organização dinamicamente (realm Keycloak = subdomain)
 * Prioridade: 1) URL param ?org= 2) simulated_subdomain 3) persisted 4) hostname 5) fallback 'backoffice'
 */
export const getOrganizationFromSubdomain = (): string => {
  if (typeof window === 'undefined') return 'backoffice';

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const orgParam = urlParams.get('org');
    if (orgParam) return orgParam;
  } catch {
    /* ignore */
  }

  const simulated = safeGetItem(window.localStorage, SIMULATED_SUBDOMAIN_KEY);
  if (simulated) return simulated;

  const persisted = getPersistedSubdomain();
  if (persisted) return persisted;

  const hostname = window.location.hostname;
  if (hostname.includes('.localhost')) return hostname.split('.')[0];
  if (hostname.includes('.spatuno.com')) return hostname.split('.')[0];

  return 'backoffice';
};

/** Grava o realm usado na autenticação OIDC (fixado no login, não muda com impersonate). */
export const persistAuthRealm = (realm: string) => {
  if (typeof window === 'undefined') return;
  safeSetItem(window.localStorage, AUTH_REALM_KEY, realm);
};

export const getPersistedAuthRealm = (): string | null => {
  if (typeof window === 'undefined') return null;
  return safeGetItem(window.localStorage, AUTH_REALM_KEY);
};

export const clearAuthRealm = () => {
  if (typeof window === 'undefined') return;
  safeRemoveItem(window.localStorage, AUTH_REALM_KEY);
};

/**
 * Resolve o realm OIDC para autenticação. Ignora simulated_subdomain
 * para que impersonação não troque o realm do sysadmin.
 *
 * Prioridade:
 *  1) ?org= na URL (login inicial)
 *  2) Hostname em produção (backoffice.spatuno.com → "backoffice")
 *  3) auth_realm persistido (gravado no login, essencial para localhost)
 *  4) Fallback "backoffice"
 */
export const getAuthRealm = (): string => {
  if (typeof window === 'undefined') return 'backoffice';

  try {
    const orgParam = new URLSearchParams(window.location.search).get('org');
    if (orgParam) return orgParam;
  } catch {
    /* ignore */
  }

  const hostname = window.location.hostname;
  if (hostname.includes('.spatuno.com')) return hostname.split('.')[0];
  if (hostname.includes('.localhost')) return hostname.split('.')[0];

  const persisted = getPersistedAuthRealm();
  if (persisted) return persisted;

  return 'backoffice';
};


